import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, ArrowUpRight } from 'lucide-react';

const MaskedLine = ({ children, delay = 0, className = '' }) => (
  <span className="mask-line">
    <motion.span
      className={`block ${className}`}
      initial={{ y: '110%' }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden bg-bark">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <video
          autoPlay muted loop playsInline preload="metadata"
          poster="/assets/hero/pc.webp"
          className="absolute inset-0 w-full h-full object-cover object-center hidden md:block"
          data-testid="hero-media-desktop"
        >
          <source src="/assets/hero/hero-pc.mp4" type="video/mp4" />
        </video>
        <video
          autoPlay muted loop playsInline preload="metadata"
          poster="/assets/hero/phone.webp"
          className="absolute inset-0 w-full h-full object-cover object-center md:hidden"
          data-testid="hero-media-mobile"
        >
          <source src="/assets/hero/hero-mobile.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-bark/60 via-bark/25 to-bark/80" />
      </motion.div>

      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-24 lg:pb-28">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.8 }}
          className="text-[10px] sm:text-[11px] tracking-[0.34em] text-sand/70 font-medium mb-6"
          data-testid="hero-meta"
        >
          VAJ PRAKRUTI HOMES · DEVELOPER
        </motion.p>

        <h1 className="font-heading font-extrabold tracking-tighter text-sand leading-[0.92] text-[15vw] sm:text-[11vw] lg:text-[8.5rem]" data-testid="hero-headline">
          <MaskedLine delay={0.15}>LEAD A</MaskedLine>
          <MaskedLine delay={0.3} className="font-serif italic font-medium text-terra">Better</MaskedLine>
          <MaskedLine delay={0.45}>FUTURE<span className="text-terra">.</span></MaskedLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5"
        >
          <Link to="/projects" data-testid="hero-explore-projects-btn"
            className="group bg-sand text-ink text-[11px] font-medium tracking-[0.2em] px-8 py-4 flex items-center gap-2 w-max hover:bg-terra hover:text-white transition-colors duration-300">
            EXPLORE PROJECTS <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link to="/enquiry" data-testid="hero-enquiry-btn"
            className="border border-sand/50 text-sand text-[11px] font-medium tracking-[0.2em] px-8 py-4 w-max hover:bg-sand hover:text-ink transition-colors duration-300">
            ENQUIRY
          </Link>
          <p className="text-sm text-sand/60 max-w-xs leading-relaxed sm:ml-6">
            NA-approved, Vaastu-compliant gated layouts — own your plot with EMI facility.
          </p>
          <a href="https://youtu.be/-A1iDrwSMv8" target="_blank" rel="noopener noreferrer" data-testid="hero-watch-film-link"
            className="text-[10px] tracking-[0.24em] text-sand/50 font-medium hover:text-terra transition-colors duration-300 w-max border-b border-sand/20 pb-1">
            WATCH THE FILM ↗
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
        className="absolute bottom-8 right-6 lg:right-10 z-10 text-sand/60 hidden sm:flex items-center gap-3"
      >
        <span className="text-[10px] tracking-[0.3em]">SCROLL</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.div>
    </section>
  );
};
